import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import LoyaltyProductLogo from 'loyalty/components/LoyaltyProductLogo'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import Image from 'components/Common/Image'
import { rem } from 'polished'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { tierGradientBackgroundStyle, tierLightBackgroundStyle, tierLightContrastColorStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import LoyaltyInfiniteBrandLogo from 'loyalty/components/LoyaltyInfiniteBrandLogo'
import LoyaltySwitchMembershipRegionPanel from 'loyalty/components/LoyaltySwitchMembershipRegionPanel'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const HeroSection = styled.div`
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const HeroGrid = styled.div`
  display: grid;
  gap: ${rem(24)};
  padding: ${rem(24)} ${rem(8)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 2fr 3fr;
    gap: ${rem(40)};
    align-items: center;
    padding: ${rem(40)} 0;
  }
`

const BackLinkBar = styled.div`
  padding: ${rem(12)} 0;
`

const BackLink = styled(ClickableLink)`
  &:hover {
    text-decoration: underline;
  }
`

const MobileImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 5 / 2;
  overflow: hidden;
`

const DesktopImageContainer = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
  height: ${rem(290)};

  &.wrong-region {
    height: ${rem(351)};
  }
`

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(40)};
`

const ContentAndCta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
`

const LoungePassBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding-block: ${rem(8)};
  padding-inline: ${rem(8)} ${rem(16)};
  border-radius: ${rem(99)};

  ${tierLightBackgroundStyle}
  ${tierLightContrastColorStyle}
`

const BadgeContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const BadgeStats = styled.div``

const LOUNGE_DIRECTORY_ID = 'lounges-directory'

function handleBrowseLoungesClick() {
  document.getElementById(LOUNGE_DIRECTORY_ID)?.scrollIntoView({ behavior: 'smooth' })
}

function LoungePassesHeroSection() {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isInLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const loyaltyRegion = useAppSelector(getLoyaltyAccountRegion)
  const programName = getLoyaltyProgramDisplayName()
  const showRegionSwitchCard = isLoggedIn && hasLoyaltyAccount && !!loyaltyRegion && !isInLoyaltyRegion
  const isDarkTier = tier === 'platinum' || tier === 'infinite'
  const loungePassBenefit = useLoyaltyUserBenefitView('lounge_pass')
  const statusReviewDate = useAppSelector(state => state.loyalty.account.data?.statusReviewDate)

  const isUnlimited = loungePassBenefit?.benefit.unlimited ?? false
  const remaining = isUnlimited ? undefined : (loungePassBenefit?.benefit.remaining ?? 0)
  const redeemed = isUnlimited ? undefined : ((loungePassBenefit?.benefit.limit ?? 0) - (loungePassBenefit?.benefit.remaining ?? 0))

  const passCountStats = isUnlimited
    ? <BodyText variant="medium" weight="bold">
        <FormattedMessage
          id="URGSGDDNclkTvOOsZvW2"
          defaultMessage="Unlimited"
          description="Unlimited lounge passes label in the hero benefit badge"
        />
    </BodyText>
    : <Group direction="horizontal" gap={4} verticalAlign="center">
        <BodyText variant="medium" weight="bold">{remaining}</BodyText>
        <BodyText variant="medium">
          <FormattedMessage
            id="uOXjPnORDDEbvO1Ofj9A"
            defaultMessage="Remaining"
            description="Remaining lounge passes count label in the hero benefit badge"
          />
        </BodyText>
        <BodyText variant="medium">
          <FormattedMessage
            id="_pdyOlB8F2otequIwl0m"
            defaultMessage="·"
            description="Separator dot between remaining and redeemed lounge pass counts"
          />
        </BodyText>
        <BodyText variant="medium" weight="bold">{redeemed}</BodyText>
        <BodyText variant="medium">
          <FormattedMessage
            id="iGIsNYd879VS2CUpjWOK"
            defaultMessage="Redeemed"
            description="Redeemed lounge passes count label in the hero benefit badge"
          />
        </BodyText>
    </Group>

  const loungePassStats = isLoggedIn && loungePassBenefit
    ? <LoungePassBadge className={`tier-${tier}`}>
        <LoyaltyTierIcon tier={tier} size={24} />
        <BadgeContent>
          <BadgeStats>
            {passCountStats}
          </BadgeStats>
          {statusReviewDate && <Group direction="horizontal" gap={4} verticalAlign="center">
            <BodyText variant="medium">
              <FormattedMessage
                id="JtG98h1nXSGpuyfoZnYN"
                defaultMessage="Expires"
                description="Expiry label for lounge passes in the hero benefit badge"
              />
            </BodyText>
            <BodyText variant="medium" weight="bold">
              <FormatDateTime value={statusReviewDate} format="long-month-long-year" />
            </BodyText>
          </Group>}
        </BadgeContent>
    </LoungePassBadge>
    : <Group direction="horizontal" gap={8} verticalAlign="center">
        <Caption variant="medium" weight="bold">
          <FormattedMessage
            id="imJuK8VxhiWojpV0mQTO"
            defaultMessage="AVAILABLE FOR:"
            description="Label preceding the tier eligibility badges on the lounge passes page"
          />
        </Caption>
        <Group direction="horizontal" gap={4}>
          <LoyaltyTierLabel tier="silver" kind="primary">
            <FormattedMessage
              id="2eZqr7azXapEJdfE18b3"
              defaultMessage="SILVER"
              description="Canonical Silver tier eligibility badge label on the lounge passes page. Do not translate; keep as SILVER in every locale"
            />
          </LoyaltyTierLabel>
          <LoyaltyTierLabel tier="gold" kind="primary">
            <FormattedMessage
              id="8ZhNg6rrDGLonuRpYA5X"
              defaultMessage="GOLD"
              description="Canonical Gold tier eligibility badge label on the lounge passes page. Do not translate; keep as GOLD in every locale"
            />
          </LoyaltyTierLabel>
          <LoyaltyTierLabel tier="platinum" kind="primary">
            <FormattedMessage
              id="n2ddew8oqMWa1nBPLqye"
              defaultMessage="PLATINUM"
              description="Canonical Platinum tier eligibility badge label on the lounge passes page. Do not translate; keep as PLATINUM in every locale"
            />
          </LoyaltyTierLabel>
        </Group>
    </Group>

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <CSSBreakpoint max="mobile">
        <LayoutContainer variant="large">
          <BackLinkBar>
            <BackLink to={`/${LOYALTY_LANDING_PAGE}#benefits`}>
              <BodyTextBlock variant="medium" startIcon={<LineAngleLeftIcon />}>
                <FormattedMessage
                  id="uyIL-7CbftRGyVM3tuGP"
                  defaultMessage="All benefits"
                  description="Back link to all Société benefits from lounge passes page"
                />
              </BodyTextBlock>
            </BackLink>
          </BackLinkBar>
        </LayoutContainer>
        <MobileImageContainer>
          <Image
            id="rq13k5lzgr8oxoz3a2"
            alt=""
            fit="center"
            gravity="center"
            aspectRatio="5:2"
            width={750}
            loading="eager"
            dpr={2}
          />
        </MobileImageContainer>
      </CSSBreakpoint>
      <LayoutContainer variant="large">
        <HeroGrid>
          <HeroContent>
            <CSSBreakpoint min="tablet">
              <BackLink to={`/${LOYALTY_LANDING_PAGE}#benefits`}>
                <BodyTextBlock variant="medium" startIcon={<LineAngleLeftIcon />}>
                  <FormattedMessage
                    id="uyIL-7CbftRGyVM3tuGP"
                    defaultMessage="All benefits"
                    description="Back link to all Société benefits from lounge passes page"
                  />
                </BodyTextBlock>
              </BackLink>
            </CSSBreakpoint>
            <ContentAndCta>
              <VerticalSpacer gap={20}>
                {isInfiniteTier && <LoyaltyInfiniteBrandLogo height={28} />}
                {!isInfiniteTier && <LoyaltyProductLogo height={26} />}
                <VerticalSpacer gap={20}>
                  <VerticalSpacer gap={8}>
                    <Heading variant="heading2">
                      <FormattedMessage
                        id="vI1lgQfuV9mjbFDizTjf"
                        defaultMessage="Free airport lounge passes"
                        description="Main hero heading displayed at the top of the lounge passes benefit page"
                      />
                    </Heading>
                    <BodyText variant="large">
                      <FormattedMessage
                        id="8lK_eCkQA4LER1z98p2c"
                        defaultMessage="Unwind with a drink, refreshments and plenty of space to relax before your international flight."
                        description="Hero description on the lounge passes benefit page"
                      />
                    </BodyText>
                  </VerticalSpacer>
                  {showRegionSwitchCard && <LoyaltySwitchMembershipRegionPanel programName={programName} userTier={tier} standalone />}
                  {!showRegionSwitchCard && !isInfiniteTier && loungePassStats}
                </VerticalSpacer>
              </VerticalSpacer>
              {!showRegionSwitchCard && <div>
                <TextButton
                  kind="secondary"
                  variant={isDarkTier ? 'ghost' : 'default'}
                  fit="mobile-full-width"
                  onClick={handleBrowseLoungesClick}
                >
                  <FormattedMessage
                    id="76Im4lbrTifopOT7aHXR"
                    defaultMessage="Browse lounges"
                    description="CTA button on the lounge passes hero that scrolls to the directory section"
                  />
                </TextButton>
              </div>}
            </ContentAndCta>
          </HeroContent>
          <CSSBreakpoint min="tablet">
            <DesktopImageContainer className={showRegionSwitchCard ? 'wrong-region' : undefined}>
              <Image
                id="rq13k5lzgr8oxoz3a2"
                alt=""
                fit="center"
                loading="eager"
                dpr={2}
              />
            </DesktopImageContainer>
          </CSSBreakpoint>
        </HeroGrid>
      </LayoutContainer>
    </HeroSection>
  </ProductPaletteProvider>
}

export { LOUNGE_DIRECTORY_ID }
export default LoungePassesHeroSection
